import { makeAutoObservable } from 'mobx';
import i18n from '../../../i18n';
import { HistoryCancelled } from '../../../models/fixed/data-analysis';
import apiManage from '../../../request';
import { SortType, handleSort } from '../../../constants/tool-order';
import { exportExcel, formatEUR } from '../../../constants/tool.fn';
import GlobalNotification from '../../../component/common/snackbar';
import { FetchCancelGate } from '../../../utils/fetch-cancel-gate';

class StaffDataStore {
    // loading
    loading: boolean = true;
    // 员工行为分析
    data: HistoryCancelled[] = []
    // 排序
    sortKeyValue: SortType = {}
    // 源数据
    dataSource: HistoryCancelled[] = []
    // 是否成功获取了数据
    errorMessage: string = '';

    /** 列表请求取消门闩（日期快速切换时 cancel 上一请求） */
    private listFetchGate = new FetchCancelGate();

    constructor() {
        makeAutoObservable(this)
    }

    /**
     * 获取数据
     */
    async fetchData(stringId: string, startDate: string, endDate: string) {
        const { cancelToken, seq } = this.listFetchGate.begin();
        this.setLoading(true);
        this.setErrorMessage('');
        try {
            const result = await apiManage.get_data_analysis_history_cancelled_period({
                string_id: stringId,
                start: startDate,
                end: endDate,
            }, { cancelToken });
            if (!this.listFetchGate.isLatest(seq)) {
                return;
            }
            if (!result?.error) {
                /** 接口数组直接使用，不再写入 `totalMaxRate`。 */
                this.setData(result)
                this.setErrorMessage('');
            } else {
                if (result.message !== 'canceled') {
                    if (result?.result?.statusCode === 404) {
                        this.setErrorMessage(i18n.t('business_analysis_page_text_restaurant_data_not_synchronized'));
                    } else {
                        this.setErrorMessage(i18n.t('business_analysis_page_text_failed_obtain_employee_analysis_data'));
                    }
                }
            }
        } finally {
            this.listFetchGate.endLoading(seq, (value) => this.setLoading(value));
        }
    }

    /**
     * 设置数据
     * @param value
     */
    setData(value: HistoryCancelled[]) {
        this.data = value
        this.dataSource = [...value]
    }

    /**
     * 更新loading
     * @param value
     */
    setLoading(value: boolean) {
        this.loading = value
    }

    /**
     * 排序
     * @param value
     */
    setSortValue(value: SortType) {
        this.sortKeyValue = value
        if (value.key && value.value) {
            this.data = handleSort(value, this.data, 'Number') || []
        } else {
            this.data = [...this.data || []]
        }
    }
    /**
     * 更新errorMessage
     */
    setErrorMessage(value: string) {
        this.errorMessage = value
    }

    /**
     * 导出当前列表为 xlsx（与表格列一致，含当前排序结果）
     */
    downloadExcel() {
        if (!this.data?.length) {
            GlobalNotification.warning(i18n.t('general_text_no_data'));
            return;
        }
        const header: string[][] = [[
            i18n.t('business_analysis_page_employee_name'),
            i18n.t('business_analysis_page_refund_quantity'),
            i18n.t('business_analysis_page_text_sum_price'),
        ]];
        const rows = this.data.map((item) => [
            String(item?.order_employee_name ?? ''),
            String(item?.quantity ?? ''),
            formatEUR(+(item?.price_total || 0)),
        ]);
        const wpx = [{ wpx: 160 }, { wpx: 120 }, { wpx: 120 }];
        exportExcel(
            header.concat(rows),
            `${i18n.t('business_analysis_page_msg_fetch_employee_analysis')}.xlsx`,
            wpx
        );
    }
}

export default StaffDataStore
